/**
 * Side-by-side comment comparison tab for the Ranking Evaluation structure.
 *
 * Shows two ranked lists of comments from different rankers in adjacent columns
 * so evaluators can compare ordering. Also owns the `SideBySideNavigationBar`
 * and `useImportConversation` helper that the Comment Scoring tab reuses for
 * conversation navigation.
 */

import type { Comment as WireComment } from '@repo/shared/orpc/procedures/comments';
import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { Banner } from 'component/banner';
import { HorizBox, Pad, Separator, VertBox } from 'component/basics';
import { CTAButton } from 'component/button';
import { Comment } from 'component/comment';
import { DropdownField } from 'component/form';
import { Checkbox } from 'component/form/checkbox';
import { ConversationPickerModal } from 'component/modal/ranking-eval/conversation-picker-modal';
import { LoadingSpinnerContainer } from 'component/progress/loading-spinner';
import { Heading, UtilityText } from 'component/text';
import type { EvalComparison, ExternalEvalSpace } from 'data/ranking-eval';
import {
    importEvalConversationAsync,
    runEvalFirstPassAsync,
    runEvalSecondPassAsync,
    submitEvalComparisonAsync,
    useEvalComparisons,
    useEvalConversation,
    useEvalConversationComments,
    useEvalCustomRankers,
    useEvalDebugInfo,
    useEvalRankerList,
    useImportableEvalConversations,
    useSelectedEvalConversationSet,
} from 'data/ranking-eval';
import { useCallback, useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import { useDatastore, useInstanceKey, usePersonaKey } from 'util/datastore';
import { fireAndForget } from 'util/hooks';
import type { FormatParams, TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';

// ============================================================================
// SIDE-BY-SIDE COMPARISON HELPERS
// ============================================================================

// Built-in, server-ranked simple sorts (no client-side ranker function — the
// key is passed straight through as `listConversationComments`'s `sort`).
const BUILTIN_RANKERS = [
    { key: 'most-recent', labelText: 'Most Recent', isLLM: false },
    { key: 'most-upvoted', labelText: 'Most Upvoted', isLLM: false },
    { key: 'most-reactions', labelText: 'Most Reactions', isLLM: false },
];

type AvailableRanker = {
    key: string;
    labelText: string;
    isLLM: boolean;
};

// Hook to get all available rankers (built-in + LLM configs from server +
// custom)
function useAvailableRankers(): AvailableRanker[] {
    const result = useEvalRankerList();
    const customRankerList = useEvalCustomRankers();

    const llmRankers: AvailableRanker[] = result
        .filter((r) => r.key !== 'most-reacted') // skip the default since we have built-in equivalents
        .map((r) => ({ key: `llm:${r.key}`, labelText: r.name, isLLM: true }));

    const customRankers: AvailableRanker[] = customRankerList
        .filter((r) => r.enabled)
        .map((r) => ({ key: `custom:${r.key}`, labelText: r.name, isLLM: true }));

    return [...BUILTIN_RANKERS, ...llmRankers, ...customRankers];
}

// Ranker selector dropdown
function RankerSelector({
    value,
    onChange,
    availableRankers,
}: {
    value: string;
    onChange: (value: string) => void;
    availableRankers: AvailableRanker[];
}) {
    const options = availableRankers.map((ranker) => ({
        key: ranker.key,
        text: ranker.labelText,
    }));

    return (
        <View style={{ minWidth: 180 }}>
            <DropdownField
                onChange={onChange}
                options={options}
                placeholder={t('ranking-eval.select_ranker', 'Select ranker')}
                value={value}
            />
        </View>
    );
}

const useCommentStyles = createThemedStyles((tokens) => ({
    column: {
        flex: 1,
        maxWidth: 560,
    },
    verticalSeparator: {
        width: 1,
        backgroundColor: tokens.colors.colorBorderPrimary,
        alignSelf: 'stretch',
    },
    rankBadge: {
        backgroundColor: tokens.colors.colorSurfaceSecondary,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 4,
        alignSelf: 'flex-start',
    },
    debugRow: {
        backgroundColor: tokens.colors.colorSurfaceSecondary,
        borderRadius: 4,
        paddingHorizontal: 12,
        paddingVertical: 6,
        marginTop: 4,
    },
}));

// Vote button for "This is best" selection
function VoteButton({
    label,
    formatParams,
    selected,
    onPress,
}: {
    label: TLabel;
    formatParams?: FormatParams;
    selected: boolean;
    onPress: () => void;
}) {
    return <CTAButton formatParams={formatParams} label={label} onPress={onPress} type={selected ? 'primary' : 'secondary'} />;
}

// ============================================================================
// SIDE-BY-SIDE CONTENT (Instance-based)
// ============================================================================

function totalReactions(comment: WireComment): number {
    return Object.values(comment.reactionCounts).reduce((sum, count) => sum + count, 0);
}

// Compute debug info for a given ranker applied to comments. The upvote and
// reaction tallies come off the ranked comments themselves — the same
// server-computed counts the built-in rankers ordered by.
function computeDebugInfo(
    rankerKey: string,
    comments: WireComment[],
    llmScores?: Record<string, { score: number; reasoning?: string }>,
): Record<string, string> {
    const info: Record<string, string> = {};
    if (rankerKey === 'most-recent') {
        for (const c of comments) {
            const date = new Date(c.createdAt);
            info[c.commentKey] = `time: ${date.toLocaleString()}`;
        }
    } else if (rankerKey === 'most-upvoted') {
        for (const c of comments) {
            info[c.commentKey] = `upvotes: ${c.upvoteCount}`;
        }
    } else if (rankerKey === 'most-reactions') {
        for (const c of comments) {
            info[c.commentKey] = `reactions: ${totalReactions(c)}`;
        }
    } else if ((rankerKey.startsWith('llm:') || rankerKey.startsWith('custom:')) && llmScores) {
        for (const c of comments) {
            const score = llmScores[c.commentKey];
            const parts = [`score: ${score?.score ?? '?'}`];
            if (score?.reasoning) {
                parts.push(score.reasoning);
            }
            info[c.commentKey] = parts.join(' — ');
        }
    }
    return info;
}

// Debug row shown below a comment when debug info is enabled
function DebugRow({ text }: { text: string }) {
    const CommentStyles = useCommentStyles();
    const { colors } = useTheme();
    return (
        <View style={CommentStyles.debugRow}>
            <UtilityText color={colors.colorTextSecondary} text={text} type='tiny' />
        </View>
    );
}

type RankingProgress = {
    scored: number;
    total: number;
    remaining: number;
};

// Renders a column of ranked comments
function RankedCommentColumn({
    rankedComments,
    showDebugInfo,
    debugInfo,
    secondPassReasoning,
    loading,
    progress,
}: {
    rankedComments: WireComment[];
    showDebugInfo?: boolean;
    debugInfo?: Record<string, string>;
    secondPassReasoning?: string;
    loading?: boolean;
    progress?: RankingProgress | null;
}) {
    const CommentStyles = useCommentStyles();
    const { colors } = useTheme();
    const topLevelComments = rankedComments.filter((c) => c.parentKey == null).slice(0, 10);

    if (loading) {
        let label = t('ranking-eval.ranking_starting', 'Starting ranking...');
        let formatParams: { scored?: number; total?: number } | undefined;
        if (progress && progress.remaining > 0) {
            label = t('ranking-eval.ranking_scoring', 'Scoring comments... {scored} of {total}');
            formatParams = { scored: progress.scored, total: progress.total };
        } else if (progress && progress.remaining === 0) {
            label = t('ranking-eval.ranking_selecting_top_10', 'Selecting top 10... this may take a while');
        }
        return (
            <View style={CommentStyles.column}>
                <LoadingSpinnerContainer formatParams={formatParams} label={label} />
            </View>
        );
    }

    return (
        <View style={CommentStyles.column}>
            <VertBox gap={0}>
                {secondPassReasoning && showDebugInfo && (
                    <View style={CommentStyles.debugRow}>
                        <UtilityText label={t('ranking-eval.top_ten_reasoning', 'Top 10 reasoning:')} type='tiny' weight='medium' />
                        <Pad size={4} />
                        <UtilityText color={colors.colorTextSecondary} numberOfLines={5} text={secondPassReasoning} type='tiny' />
                    </View>
                )}
                {topLevelComments.map((comment, index) => (
                    <View key={comment.commentKey}>
                        {index > 0 && <Separator />}
                        <Pad size={16} />
                        {showDebugInfo && (
                            <HorizBox center gap={8}>
                                <View style={CommentStyles.rankBadge}>
                                    <UtilityText text={`#${index + 1}`} type='tiny' weight='medium' />
                                </View>
                            </HorizBox>
                        )}
                        <Comment commentTree={{ comment, replies: [], boosted: false }} />
                        {showDebugInfo && debugInfo?.[comment.commentKey] && <DebugRow text={debugInfo[comment.commentKey]} />}
                        <Pad size={16} />
                    </View>
                ))}
            </VertBox>
        </View>
    );
}

function RefreshCommentsRow({
    onRefresh,
    refreshing,
    importedAt,
}: {
    onRefresh?: () => void;
    refreshing?: boolean;
    importedAt?: string | null;
}) {
    const { colors } = useTheme();
    if (!onRefresh && !importedAt) return null;
    const importedAtLabel = t('ranking-eval.imported_at', 'Fetched {time}');
    const importedAtFormat = importedAt ? { time: new Date(importedAt).toLocaleString() } : undefined;
    return (
        <HorizBox center gap={12}>
            {onRefresh && (
                <CTAButton
                    disabled={refreshing}
                    label={
                        refreshing
                            ? t('ranking-eval.refresh_comments_in_progress', 'Refreshing comments...')
                            : t('ranking-eval.refresh_comments', 'Refresh comments')
                    }
                    onPress={onRefresh}
                    type='secondary'
                />
            )}
            {importedAt && (
                <UtilityText color={colors.colorTextSecondary} formatParams={importedAtFormat} label={importedAtLabel} type='small' />
            )}
        </HorizBox>
    );
}

export function SideBySideNavigationBar({
    instanceKey,
    selectedKeys,
    allConversations,
    onNavigate,
    onRefresh,
    refreshing,
    importedAt,
}: {
    instanceKey: string;
    selectedKeys: string[];
    allConversations: Record<string, ExternalEvalSpace>;
    onNavigate: (key: string) => void;
    onRefresh?: () => void;
    refreshing?: boolean;
    importedAt?: string | null;
}) {
    const [showPicker, setShowPicker] = useState(false);
    const { colors } = useTheme();
    const navKeys = selectedKeys.length > 0 ? selectedKeys : Object.keys(allConversations);
    const currentIndex = navKeys.indexOf(instanceKey);
    const hasPrevious = currentIndex > 0;
    const hasNext = currentIndex >= 0 && currentIndex < navKeys.length - 1;

    return (
        <>
            <VertBox gap={8}>
                <HorizBox center gap={12}>
                    <CTAButton
                        disabled={!hasPrevious}
                        label={t('ranking-eval.prev_button', '← Prev')}
                        onPress={() => onNavigate(navKeys[currentIndex - 1])}
                        type='secondary'
                    />
                    <CTAButton
                        label={t('ranking-eval.pick_conversation', 'Choose conversation')}
                        onPress={() => setShowPicker(true)}
                        type='secondary'
                    />
                    <CTAButton
                        disabled={!hasNext}
                        label={t('ranking-eval.next_button', 'Next →')}
                        onPress={() => onNavigate(navKeys[currentIndex + 1])}
                        type='secondary'
                    />
                    <UtilityText
                        color={colors.colorTextSecondary}
                        text={navKeys.length > 0 ? `${currentIndex + 1} of ${navKeys.length}` : ''}
                        type='small'
                    />
                </HorizBox>
                <RefreshCommentsRow importedAt={importedAt} onRefresh={onRefresh} refreshing={refreshing} />
            </VertBox>
            {showPicker && (
                <ConversationPickerModal
                    allConversations={allConversations}
                    instanceKey={instanceKey}
                    onClose={() => setShowPicker(false)}
                    onNavigate={onNavigate}
                    selectedKeys={selectedKeys}
                />
            )}
        </>
    );
}

// Ranker selectors and debug toggle bar
function SideBySideControls({
    rankerAKey,
    rankerBKey,
    onChangeA,
    onChangeB,
    showDebugInfo,
    onToggleDebug,
    availableRankers,
}: {
    rankerAKey: string;
    rankerBKey: string;
    onChangeA: (value: string) => void;
    onChangeB: (value: string) => void;
    showDebugInfo: boolean;
    onToggleDebug: () => void;
    availableRankers: AvailableRanker[];
}) {
    return (
        <HorizBox center spread wide>
            <RankerSelector availableRankers={availableRankers} onChange={onChangeA} value={rankerAKey} />
            <Checkbox label={t('ranking_eval.show_debug_info', 'Show debug info')} onChange={onToggleDebug} value={showDebugInfo} />
            <RankerSelector availableRankers={availableRankers} onChange={onChangeB} value={rankerBKey} />
        </HorizBox>
    );
}

// Vote buttons for side-by-side comparison
function SideBySideVoteButtons({
    rankerA,
    rankerB,
    vote,
    onVote,
}: {
    rankerA: AvailableRanker | undefined;
    rankerB: AvailableRanker | undefined;
    vote: 'a_better' | 'tie' | 'b_better' | null;
    onVote: (vote: 'a_better' | 'tie' | 'b_better') => void;
}) {
    return (
        <HorizBox spread wide>
            <VoteButton
                formatParams={{ ranker: rankerA?.labelText || 'A' }}
                label={t('ranking-eval.vote_a_best', '{ranker} is Best')}
                onPress={() => onVote('a_better')}
                selected={vote === 'a_better'}
            />
            <VoteButton label={t('ranking-eval.vote_tie', 'Same')} onPress={() => onVote('tie')} selected={vote === 'tie'} />
            <VoteButton
                formatParams={{ ranker: rankerB?.labelText || 'B' }}
                label={t('ranking-eval.vote_b_best', '{ranker} is Best')}
                onPress={() => onVote('b_better')}
                selected={vote === 'b_better'}
            />
        </HorizBox>
    );
}

// Show other users' ratings for this ranker pair
function OthersRatings({
    conversationComparisons,
    rankerAKey,
    rankerBKey,
    rankerA,
    rankerB,
    personaKey,
    hasVoted,
}: {
    conversationComparisons: Record<string, EvalComparison> | null | undefined;
    rankerAKey: string;
    rankerBKey: string;
    rankerA: AvailableRanker | undefined;
    rankerB: AvailableRanker | undefined;
    personaKey: string | null;
    hasVoted: boolean;
}) {
    const { colors } = useTheme();
    if (!hasVoted) return null;

    const othersVotes = Object.values(conversationComparisons || {}).filter(
        (c) => c.rankerA === rankerAKey && c.rankerB === rankerBKey && c.from !== personaKey,
    );

    const winnerLabel = (winner: string) => {
        if (winner === 'a_better') return rankerA?.labelText || 'A';
        if (winner === 'b_better') return rankerB?.labelText || 'B';
        return 'Tie';
    };

    return (
        <Banner color={colors.colorSurfaceSecondary}>
            <VertBox gap={8}>
                <UtilityText label={t('ranking-eval.others_ratings', "Others' ratings")} type='small' weight='medium' />
                {othersVotes.length === 0 ? (
                    <UtilityText
                        color={colors.colorTextSecondary}
                        label={t('ranking-eval.no_other_ratings', 'No other ratings yet.')}
                        type='small'
                    />
                ) : (
                    <VertBox gap={4}>
                        {othersVotes.map((vote) => (
                            <UtilityText key={vote.key} text={`${vote.fromName || vote.from}: ${winnerLabel(vote.winner)}`} type='small' />
                        ))}
                    </VertBox>
                )}
            </VertBox>
        </Banner>
    );
}

// Two columns of ranked comments side by side
function SideBySideColumns({
    rankedCommentsA,
    rankedCommentsB,
    showDebugInfo,
    debugInfoA,
    debugInfoB,
    secondPassReasoningA,
    secondPassReasoningB,
    loadingA,
    loadingB,
    progressA,
    progressB,
}: {
    rankedCommentsA: WireComment[];
    rankedCommentsB: WireComment[];
    showDebugInfo: boolean;
    debugInfoA: Record<string, string>;
    debugInfoB: Record<string, string>;
    secondPassReasoningA?: string;
    secondPassReasoningB?: string;
    loadingA?: boolean;
    loadingB?: boolean;
    progressA?: RankingProgress | null;
    progressB?: RankingProgress | null;
}) {
    const CommentStyles = useCommentStyles();
    return (
        <HorizBox gap={20} wide>
            <RankedCommentColumn
                debugInfo={debugInfoA}
                loading={loadingA}
                progress={progressA}
                rankedComments={rankedCommentsA}
                secondPassReasoning={secondPassReasoningA}
                showDebugInfo={showDebugInfo}
            />
            <View style={CommentStyles.verticalSeparator} />
            <RankedCommentColumn
                debugInfo={debugInfoB}
                loading={loadingB}
                progress={progressB}
                rankedComments={rankedCommentsB}
                secondPassReasoning={secondPassReasoningB}
                showDebugInfo={showDebugInfo}
            />
        </HorizBox>
    );
}

// Hook to import conversation data into the current instance Calls the
// server-side importConversation API, which has privileged access to write
// comments with their original author IDs (bypassing Firebase rules that
// require `from` to match the authenticated user).
export function useImportConversation() {
    const datastore = useDatastore();
    const { name, importedAt, isPending } = useEvalConversation();
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const importedRef = useRef(false);

    useEffect(() => {
        // Wait for the status to load, then skip if an import has already run
        // or one has started this session.
        if (isPending || importedAt || importedRef.current) return;

        importedRef.current = true;

        async function doImport() {
            setImporting(true);
            try {
                await importEvalConversationAsync(datastore);
            } catch (err: any) {
                setError(err.message || 'Failed to import conversation');
            } finally {
                setImporting(false);
            }
        }

        fireAndForget(doImport());
    }, [isPending, importedAt, datastore]);

    const reimport = useCallback(async () => {
        setImporting(true);
        setError(null);
        try {
            await importEvalConversationAsync(datastore, { force: true });
        } catch (err: any) {
            setError(err?.message || 'Failed to refresh conversation');
        } finally {
            setImporting(false);
        }
    }, [datastore]);

    return { name, loading: isPending || importing, error, reimport, importedAt };
}

// An LLM/custom ranker's underlying key, without its `llm:`/`custom:` prefix
// — `null` for a simple (non-scored) ranker.
function toActualRankerKey(rankerKey: string): string | null {
    return rankerKey.startsWith('llm:') || rankerKey.startsWith('custom:') ? rankerKey.replace(/^(llm|custom):/, '') : null;
}

// Runs the first-pass/second-pass oRPC scoring for an LLM/custom ranker
// (batched, with progress), then fetches the server-ranked tree for
// `rankerKey` — simple rankers (most-recent/most-upvoted/most-reactions) skip
// scoring entirely and fetch immediately. This hook only drives ranking; the
// debug overlay's per-comment scores and top-10 reasoning are a separate
// concern fetched by `useEvalDebugInfo`.
function useLLMRanking(rankerKey: string) {
    const datastore = useDatastore();
    const isLLM = rankerKey.startsWith('llm:') || rankerKey.startsWith('custom:');
    const actualRankerKey = toActualRankerKey(rankerKey);
    const sort = actualRankerKey ?? rankerKey;

    const [scoring, setScoring] = useState(isLLM);
    const [scoringError, setScoringError] = useState<string | null>(null);
    const [progress, setProgress] = useState<RankingProgress | null>(null);
    const triggeredRef = useRef<string | null>(null);
    const cancelledRef = useRef(false);

    useEffect(() => {
        if (!actualRankerKey || triggeredRef.current === actualRankerKey) return;
        const rankerKeyToScore = actualRankerKey;

        triggeredRef.current = actualRankerKey;
        cancelledRef.current = false;
        setScoring(true);
        setScoringError(null);
        setProgress(null);

        const MAX_SCORING_ITERATIONS = 100;

        async function scoreIncrementally() {
            let status = 'in_progress';
            let iterations = 0;
            while (status !== 'scoring_complete' && !cancelledRef.current && iterations < MAX_SCORING_ITERATIONS) {
                iterations++;
                try {
                    const result = await runEvalFirstPassAsync(datastore, { rankerKey: rankerKeyToScore });
                    status = result.status;
                    setProgress({ scored: result.total - result.remaining, total: result.total, remaining: result.remaining });
                } catch (err: any) {
                    setScoringError(err?.message || 'Scoring failed');
                    setScoring(false);
                    return;
                }
            }

            if (cancelledRef.current) return;

            setProgress((prev) => (prev ? { ...prev, remaining: 0 } : null));
            try {
                await runEvalSecondPassAsync(datastore, { rankerKey: rankerKeyToScore });
            } catch (err: any) {
                setScoringError(err?.message || 'Second pass failed');
            }
            setScoring(false);
        }

        fireAndForget(scoreIncrementally());

        return () => {
            cancelledRef.current = true;
        };
    }, [actualRankerKey, datastore]);

    const { tree, isPending: treePending, error: rankError } = useEvalConversationComments(scoring ? null : sort);

    return {
        tree,
        loading: scoring || treePending,
        error: scoringError ?? (rankError ? rankError.message : null),
        progress,
    };
}

// eslint-disable-next-line max-lines-per-function
export function SideBySideContent() {
    const datastore = useDatastore();
    const instanceKey = useInstanceKey();
    const { name, loading: importing, error: importError, reimport, importedAt } = useImportConversation();
    const conversationName = name || instanceKey;
    const selectedSet = useSelectedEvalConversationSet();
    const selectedKeys = selectedSet?.conversationKeys || [];
    const allConversations = useImportableEvalConversations();
    const availableRankers = useAvailableRankers();

    const [rankerAKey, setRankerAKey] = useState('most-recent');
    const [rankerBKey, setRankerBKey] = useState('most-upvoted');
    const [showDebugInfo, setShowDebugInfo] = useState(false);

    const rankingA = useLLMRanking(rankerAKey);
    const rankingB = useLLMRanking(rankerBKey);
    const conversationComparisons = useEvalComparisons();
    const personaKey = usePersonaKey();

    // Debug-only: per-comment scores/reasoning for an LLM/custom ranker, only
    // fetched while the debug overlay is actually shown.
    const debugInfoLLMA = useEvalDebugInfo(showDebugInfo ? toActualRankerKey(rankerAKey) : null);
    const debugInfoLLMB = useEvalDebugInfo(showDebugInfo ? toActualRankerKey(rankerBKey) : null);

    const myRecordKey = `${personaKey}-${rankerAKey}-${rankerBKey}`;
    const myExistingVote = conversationComparisons?.[myRecordKey]?.winner || null;
    const rankerA = availableRankers.find((r) => r.key === rankerAKey);
    const rankerB = availableRankers.find((r) => r.key === rankerBKey);
    const rankedCommentsA = rankingA.tree.map((node) => node.comment);
    const rankedCommentsB = rankingB.tree.map((node) => node.comment);
    const debugInfoA = showDebugInfo ? computeDebugInfo(rankerAKey, rankedCommentsA, debugInfoLLMA?.scores) : {};
    const debugInfoB = showDebugInfo ? computeDebugInfo(rankerBKey, rankedCommentsB, debugInfoLLMB?.scores) : {};

    const handleNavigate = (key: string) =>
        datastore.gotoInstanceScreen({
            structureKey: 'ranking-eval',
            instanceKey: 'one',
            screenKey: `side-by-side/${key}`,
            replace: true,
        });

    async function handleVote(selectedVote: 'a_better' | 'tie' | 'b_better') {
        await submitEvalComparisonAsync(datastore, { rankerA: rankerAKey, rankerB: rankerBKey, winner: selectedVote });
    }

    if (importing) {
        return <LoadingSpinnerContainer label={t('ranking-eval.importing', 'Importing conversation...')} />;
    }

    if (importError) {
        return <UtilityText text={`Error: ${importError}`} type='small' />;
    }

    return (
        <VertBox gap={24}>
            <VertBox gap={8}>
                <Heading text={conversationName} type='large' />
                <UtilityText label={t('ranking-eval.side_by_side_desc', 'Compare two ranking algorithms side-by-side')} type='small' />
            </VertBox>
            <SideBySideNavigationBar
                allConversations={allConversations}
                importedAt={importedAt}
                instanceKey={instanceKey}
                onNavigate={handleNavigate}
                onRefresh={reimport}
                refreshing={importing}
                selectedKeys={selectedKeys}
            />

            {!rankingA.loading && rankedCommentsA.length === 0 ? (
                <UtilityText label={t('ranking-eval.no_comments', 'This conversation has no comments.')} type='small' />
            ) : (
                <>
                    <SideBySideControls
                        availableRankers={availableRankers}
                        onChangeA={setRankerAKey}
                        onChangeB={setRankerBKey}
                        onToggleDebug={() => setShowDebugInfo(!showDebugInfo)}
                        rankerAKey={rankerAKey}
                        rankerBKey={rankerBKey}
                        showDebugInfo={showDebugInfo}
                    />

                    <SideBySideVoteButtons onVote={handleVote} rankerA={rankerA} rankerB={rankerB} vote={myExistingVote} />
                    <OthersRatings
                        conversationComparisons={conversationComparisons}
                        hasVoted={!!myExistingVote}
                        personaKey={personaKey}
                        rankerA={rankerA}
                        rankerAKey={rankerAKey}
                        rankerB={rankerB}
                        rankerBKey={rankerBKey}
                    />

                    <SideBySideColumns
                        debugInfoA={debugInfoA}
                        debugInfoB={debugInfoB}
                        loadingA={rankingA.loading}
                        loadingB={rankingB.loading}
                        progressA={rankingA.progress}
                        progressB={rankingB.progress}
                        rankedCommentsA={rankedCommentsA}
                        rankedCommentsB={rankedCommentsB}
                        secondPassReasoningA={debugInfoLLMA?.secondPassReasoning ?? undefined}
                        secondPassReasoningB={debugInfoLLMB?.secondPassReasoning ?? undefined}
                        showDebugInfo={showDebugInfo}
                    />
                </>
            )}
        </VertBox>
    );
}
